/**
 * Copyright (c) 2018-2019 Aleksey Melnikov, True Directive Company.
 * @link https://truedirective.com/
 * @license MIT
*/
/*
  View only. Missed features:
    - Checkboxes.
    - Editing.
    - Selection.
 */
import { Component, Input, Output, ViewChild, ViewChildren, ContentChildren,
         ElementRef, QueryList, ChangeDetectorRef,  KeyValueDiffer,
         KeyValueDiffers, EventEmitter, Inject, DoCheck, OnDestroy
       } from '@angular/core';

import { Subject, Observable, Subscription } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';

import { GridPart, RenderMode, DetectionMode, ColumnType } from '@true-directive/base';
import { Column, CellPosition, GridSettings, RowLayout, DataQuery, UIAction,
         SortInfo, SortType, Filter, Selection, MenuAction, LazyLoadingMode } from '@true-directive/base';
import { PagePipe, RowCalculator } from '@true-directive/base';

import { ScrollerComponent } from './scroller.component';
import { BaseComponent } from './base.component';

import { RowDirective } from './row.directive';

import { GridStateService } from './grid-state.service';
import { InternationalizationService } from './internationalization/internationalization.service';

import { FilterTextComponent } from './filters/datatypes/filter-text.component';
import { FilterDateComponent } from './filters/datatypes/filter-date.component';
import { FilterNumberComponent } from './filters/datatypes/filter-number.component';
import { FilterBooleanComponent } from './filters/datatypes/filter-boolean.component';

import { FilterPopupComponent } from './filters/filter-popup.component';

import { MenuStarterComponent } from './controls/menu-starter.component';

@Component({
  selector: 'true-grid-view',
  templateUrl: './grid-view.component.html',
  providers: [{ provide: 'gridState', useClass: GridStateService }],
  styleUrls: ['./styles/grid.behavior.scss']
})
export class GridViewComponent extends BaseComponent implements DoCheck, OnDestroy {

  protected destroy$: Subject<boolean> = new Subject<boolean>();

  private _data: Observable<any[]> = null;
  private _dataSubscription: Subscription;

  /**
   * Grid data source. You can specify an observable object or array.
   */
  @Input('data')
  public set data(ds: any[] | Observable<any[]>) {
    // Unsubscribe from previous source
    if (this._data !== null && ds === this._data) {
      return;
    }

    if (this._data !== null) {
      this._dataSubscription.unsubscribe();
      this._data = null;
    }

    if (ds instanceof Observable) {
      // Subscribe for new data
      this._data = ds;
      this._dataSubscription = this._data
        .pipe(takeUntil(this.destroy$))
        .subscribe(data => {
        this.state.model = data;
        this.updateData();
      });
    } else {
      // Just array
      if (this.state.model !== ds) {
        this.state.model = ds;
        if (this._initialized) {
          this.updateData();
        }
      }
    }
  }

  /**
   * Data source
   * @return Array of the rows or observable object
   */
  public get data(): any[] | Observable<any[]> {
    return this._data !== null ? this._data : this.state.model;
  }

  /**
   * List of the rows to display
   * @return Array of the rows
   */
  public get rows(): any[] {
    return this.state.model;
  }

  /**
   * Resulting list of rows after applying filters and sorting
   * @return Array of the rows
   */
  public get resultRows(): any[] {
    return this.state.dataSource.resultRows;
  }

  /**
   * Current locale name
   */
  public get locale(): string {
    return this.intl.currentLocaleName;
  }

  /**
   * List of the columns
   */
  @Input('columns')
  public set columns(v: Column[]) {
    this.state.columns = v;
  }

  public get columns() {
    return this.state.columns;
  }

  /**
   * Grid's settings
   */
  @Input('settings')
  public set settings(v: GridSettings) {
    this.state.settings = v;
  }

  public get settings() {
    return this.state.settings;
  }

  /**
   * The maximum grid height, unless explicitly specified.
   */
  @Input('maxHeight')
  public maxHeight: number = null;

  /**
   * Text search data for all columns
   */
  @Input('searchString')
  get searchString() {
    return this.state.dataSource.searchString;
  }

  set searchString(v: string) {
    if (this.state.dataSource.searchString !== v) {
      this.state.dataSource.searchString = v;
      // A pause is necessary so that when printing quickly, do not perform
      // too frequent updates.
      setTimeout(() => {
        if (this.state.dataSource.searchString === v) {
          this.updateData();
        }
      }, this.state.settings.searchDelay);
    }
  }

  /**
   * Event that will be triggered when data retrieval options are changed (filters,
   * sortings)
   * @param  'dataQuery' [description]
   * @return             [description]
   */
  @Output('dataQuery')
  dataQuery: EventEmitter<DataQuery> = new EventEmitter<DataQuery>();

  @Output('queryChanged')
  queryChanged: EventEmitter<DataQuery> = new EventEmitter<DataQuery>();

  @Output()
  startProcess: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  endProcess: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  headerContextMenu: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  customCellEvent: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  menuAction: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('menuStarter', {static: true}) menuStarter: MenuStarterComponent;
  @ViewChild('filterPopup', {static: false}) filterPopup: FilterPopupComponent; // Popup div with filter options

  @ViewChild('scroller', {static: true}) scroller: ScrollerComponent;

  @ViewChild('grid', {static: true}) grid: any;
  @ViewChild('gridHeader', {static: true}) gridHeader: any;
  @ViewChild('gridData', {static: true}) gridData: any;

  @ViewChild('dragItem', {static: true}) dragItem : any; // Clone drag column / band header

  @ContentChildren(RowDirective) displayedRows_template: QueryList<RowDirective>;

  @ViewChildren('displayedRows', { read: RowDirective }) displayedRowsCenter: QueryList<RowDirective>;

  @ViewChild('customTemplate', {static: true}) customTemplate: any;

  protected uiAction: UIAction = null;
  protected _initialized: boolean = false; // Grid initialized
  private _viewInitialized: boolean = false; // View initialized
  private _lastUpdateTime: number = 0; // Last page refresh time

  // Position and time of last scroll
  protected readonly _lastScroll = { pos: -1, renderedPos: -1, time: 0 };

  public dataUpdating = false; // Updating data in progress

  get displayedRows() {
    if (this.state.settings.customTemplate) {
      return this.displayedRows_template;
    }
    return this.displayedRowsCenter;
  }

  // Page processing required
  protected need_recalc_page = true;

  private _prevOffset = -1;
  private _prevLimit = -1;

  /**
   * Current appearance css-class
   */
  private _currentAppearance = '';

  /**
   * List of rows to be rendered.
   */
  private _currentRendered: any[] = [];

  // Settings differ
  private _settingsDiffer: KeyValueDiffer<any, any>;

  // Appearance settings differ
  private _appearanceDiffer: KeyValueDiffer<any, any>;

  public viewPortLeft = -1;
  public viewPortWidth = -1;

  public RC: RowCalculator = new RowCalculator(this.state);

  public get selection(): Selection {
    return this.state.selection;
  }

  protected _headerParts(): BaseComponent[] {
    return [this.gridHeader];
  }

  protected get headerParts(): BaseComponent[] {
    return this._headerParts();
  }

  protected _dataParts(): Array<QueryList<RowDirective>> {
    return [this.displayedRowsCenter];
  }

  protected get dataParts(): Array<QueryList<RowDirective>> {
    return this._dataParts();
  }

  public immediateFilter(filter: string) {
    this.state.dataSource.searchString = filter;
    this.updateData();
  }

  /**
   * List of the rows to render
   * @return Array of the rows
   */
  public get visibleRows(): any[] {

    if (!this.state.dataSource.resultRows) {
      return [];
    }

    if (this._prevOffset !== this.state.pageInfo.offset ||
        this._prevLimit !== this.state.pageInfo.limit ||
        this.need_recalc_page) {

      // Page refresh
      this.state.lazyLoader.query(this.state.pageInfo.offset);
      const toRender: any[] = new PagePipe().transform(this.state.dataSource.resultRows, this.state.pageInfo);
      this._prevOffset = this.state.pageInfo.offset;
      this._prevLimit = this.state.pageInfo.limit;
      this._currentRendered = toRender;
      this.need_recalc_page = false;

      // Important!
      this.state.displayedStartIndex = this.state.pageInfo.offset;

      // ÐÑÐ»Ð¸ Ð¼Ñ.. ÐÐ¾ÑÐºÑÐ¾Ð»Ð»Ð¸Ð»Ð¸ Ð´Ð¾ Ð¿ÑÐµÐ´ÐµÐ»Ð°...
      // ÐÐ°Ð´Ð¾ Ð±Ñ Ð·Ð°Ð¿ÑÐ¾ÑÐ¸ÑÑ ÐµÑÐµ Ð´Ð°Ð½Ð½ÑÐµ.
    }

    return this._currentRendered;
  }

  // Keeping the height of all rows
  private saveRowHeights() {
    this.displayedRowsCenter.forEach(el => {
      let hh: number;
      if (this.state.IE) {
        hh = el.elementRef.nativeElement.getBoundingClientRect().height;
      } else {
        hh = el.elementRef.nativeElement.clientHeight;
      }
      this.RC.setRowHeight(this.state.dataSource.resultRowCount, el.axI + this.state.pageInfo.offset, hh);
    });
  }

  /**
   * Page refresh
   * @param  forceChanges Force update even if there was no scrolling
   * @param  overwork     The number of rows that will be rendered outside the viewport
   */
  public updatePage(log: string = '', forceChanges: boolean = false, overwork: any = null) {
    // Ð­ÑÐ¾ Ð½Ð°Ð´Ð¾ Ð¿ÐµÑÐµÐºÐ¸Ð½ÑÑÑ Ð² ÑÐ¾ÑÑÐ¾ÑÐ½Ð¸Ðµ
    if (this.viewPortLeft !== this.scroller.scrollLeft || this.viewPortWidth !==  this.scroller.viewPortWidth) {
      this.viewPortLeft = this.scroller.scrollLeft;
      this.viewPortWidth =  this.scroller.viewPortWidth;
      forceChanges = true;
    }

    if (!forceChanges) {
      this.saveRowHeights();
    }

    var rc = this.state.dataSource.resultRowCount;
    if (this.state.st.lazyLoading === LazyLoadingMode.FRAGMENTARY) {
      rc = this.state.dataSource.totalRowCount;
    }

    const pageChanged = this.RC.updateRenderInfo(
      rc,
      this.scroller.scrollTop,
      this.scroller.viewPortHeight,
      overwork);

    if (forceChanges || pageChanged) {
      this.detectChanges('page');
    }
  }

  /**
   * Force view update
   */
  public updateView() {
    // First we need to update all view params
    this.detectChanges('view');
    this.state.updateLayouts();
    // Then the current page. Because its calculation depends on these parameters
    this.updatePage('updateView', true);
  }

  public cellPosition(row: any, fieldName: string): CellPosition {
    const ri = this.resultRows.indexOf(row);
    return new CellPosition(row, ri, fieldName);
  }

  public startSelect(cp: CellPosition, add: boolean = false) {
    this.state.ui.startSelect(cp, add);
  }

  public proceedToSelect(cp: CellPosition) {
    this.state.ui.proceedToSelect(cp);
  }

  /**
   * Hide all buttons in column headers
   */
  public hideHeaderBtns() {
    this.headerParts.forEach(p => {
      p.hideHeaderBtns();
    });
  }

  /**
   * Asynchronous data update. The Observable returns, by subscribing to which
   * you can find out when this update ended.
   * Use this method with lazy loading settings.
   * @param  data The data (array of objects).
   * @return      Observable object whose event will occur immediately after processing.
   */
  public updateDataAsync(data: any[]): Observable<any> {
    this.data = data;
    this.startProcess.emit('DataUpdate');
    this.elementRef.nativeElement.classList.add('processing');
    const subj = this.state.updateDataAsync();
    subj.subscribe(e => {
      this.endProcess.emit('DataUpdate');
    })
    return subj;
  }

  /**
   * Updating data with external or internal change.
   */
  public updateData(async: boolean = true) {

    if (!this._initialized) {
      async = false;
    }

    // The changes are serious. Row heights update.
    this.RC.clear();

    // Through a timeout, so that when the filter is applied, the button does
    // not have time to turn off before it is shown with an accent color
    // setTimeout(() => this.hideHeaderBtns());

    if (async) {
      this.startProcess.emit('DataUpdate');
      this.elementRef.nativeElement.classList.add('processing');
      setTimeout(() => {
        this.state.updateData();
        this.endProcess.emit('DataUpdate');
      }, 1);
    } else {
      this.state.updateData(async);
    }
  }

  public updateSummaries() {
    this.state.updateSummaries();
    this.updateView();
  }

  /**
   * Received data externally (from the parent component)
   * @param  query The request in response to which the data received.
   * @param  data  Data that received in response to a given request.
   */
  public fetchData(query: DataQuery, data: any[], totalRowCount: number = null) {
    this.state.fetchData(query, data, totalRowCount);
  }

  protected renderData() {

    setTimeout(() => this.hideHeaderBtns());

    this.dataUpdating = false;
    this.elementRef.nativeElement.classList.remove('processing');

    this.need_recalc_page = true;
    // Sent for further processing (aggregation, grouping)

    // Refreshing page with forced detectChanges
    this.updatePage('renderData', true);

    // After adding data, the width of the client part of the grid may change
    // due to the appearance of a scrollbar. Therefore, check the width of the grid.
    if (this.state.settings.columnAutoWidth) {
      this.checkSize();
    }

    // The height of the data changed, but the scroll position does not.
    // Therefore, scroll the related parts.
    this.scroller.scrollParts();
  }

  private _inProcess = false;

  protected updatePageByScroll() {    
    this.updatePage('scroll', false);
    this._lastUpdateTime = Date.now();
  }

  /**
   * Scrolling data
   * @param  e Scroll event
   */
  public gridScroll(e: any) {    
    let scrollPos = e.target.scrollTop;
    let scrollPosH = e.target.scrollLeft;

    const dscrollH = scrollPosH - this.viewPortLeft;
    const dscroll = scrollPos - this._lastScroll.renderedPos;

    this._lastScroll.pos = scrollPos;

    this.RC.currentScrollPos = scrollPos;

    let needUpdate = false;

    if (Math.abs(dscroll) > this.RC.currentRH * 2) {
      // We have a stock of two lines. If the scroll is larger, then update.
      needUpdate = true;
    }

    if (Math.abs(dscrollH) > 1 && this.state.settings.renderMode === RenderMode.VISIBLE) {
      // When scrolling horizontally only if RenderMode.VISIBLE
      needUpdate = true;
    }

    if (!needUpdate) {
      // Ð·Ð°Ð¼ÐµÐ½Ð¸Ð¼ Ð½Ð° ÑÐ²Ð½Ð¾Ðµ Ð·Ð°Ð´Ð°Ð½Ð¸Ðµ margin-top Ñ
      return;
    }

    if (this._inProcess && this.state.iOS) {
      return;
    }

    if (scrollPosH < 0) {
      return;
    }

    if (scrollPos < 0 || scrollPos > (this.scroller.scrollHeight - this.scroller.viewPortHeight)) {
      return;
    }

    this._inProcess = true;

    const dt = Date.now();

    this._lastScroll.renderedPos = scrollPos;
    this._lastScroll.time = dt;

    const ms = dt - this._lastUpdateTime;
    let dms  = this.state.IE ? 40: 10;

    if (ms < dms && !this.state.safari) {
      // Delaying
      const delay = dms * 4;
      setTimeout(() => {
        if (this._lastScroll.time === dt) {
          this.updatePageByScroll();
        }
      }, delay);
      this._inProcess = false;
      return;
    }

    this.updatePageByScroll();
    this._inProcess = false;
  }

  /**
   * Triggered by automatic scrolling during area selection
   * @param  dx How many pixels are scrolled horizontally?
   */
  public gridAutoScrollX(dx: number) {
    this.gridHeader.autoScrollX(dx);
  }

  /**
   * We need call this method for the changes to take effect.
   * If dataAffected=true then re-filter and re-sort data.
   * @param  log          Reason for detecting changes
   * @param  dataAffected Is the data changed
   */
  public detectChanges(log: string = '', dataAffected: boolean = false) {
    if (dataAffected) {      
      this.updateData();
      return;
    }

    if (this.state.settings.changeDetectionMode === DetectionMode.MANUAL) {
      // Otherwise, it will check the changes. And we wonât pull the detector again..      
      this.changeDetector.detectChanges();
    }
  }

  protected displayedRowsByXY(x: number, y: number, place: GridPart = null): QueryList<RowDirective> {
    return this.displayedRows;
  }

  protected rowLayouts(rows: QueryList<RowDirective>): RowLayout[] {

    const result: RowLayout[] = [];
    const firstRow = rows.first;
    if (!firstRow) {
      return result;
    }

    let ri = this.state.pageInfo.offset;

    rows.forEach(el => {
      const rl = new RowLayout();
      rl.index = ri;
      rl.rowComponent = el;
      rl.clientRect = el.elementRef.nativeElement.getBoundingClientRect();
      result.push(rl);
      ri++;
    });

    return result;
  }

  protected rowByXY(rows: QueryList<RowDirective>, x: number, y: number): RowLayout {
    return RowLayout.rowByXY(this.rowLayouts(rows), x, y);
  }

  protected cellByXY(x: number, y:number, place: GridPart = null): CellPosition {
    const rows = this.displayedRowsByXY(x, y, place);
    const r = this.rowByXY(rows, x, y);
    if (r && r.rowComponent) {
      return this.state.ui.cellPosition(r.rowComponent.row, r.index, r.rowComponent.cellByXY(x, y));
    }
    return null;
  }

  // ÐÐµÑÐµÑÐ¸ÑÐ¾Ð²ÐºÐ° Ð²ÑÐ´ÐµÐ»ÐµÐ½Ð½Ð¾Ð³Ð¾
  protected refreshSelection(scrollTo: CellPosition) {
    this.dataParts.forEach(p =>
      p && p.forEach(el => el.setSelection())
    );
    if (scrollTo) {
      this.scrollTo(scrollTo);
    }
  }

  /**
   * ÐÑÐ¾ÐºÑÑÑÐ¸ÑÑ ÑÐºÑÐ¾Ð»Ð»Ð±Ð¾ÐºÑÑ ÑÐ°Ðº, ÑÑÐ¾Ð±Ñ Ð±ÑÐ»Ð° Ð²Ð¸Ð´Ð½Ð° ÑÑÐµÐ¹ÐºÐ°, Ð½Ð° ÐºÐ¾ÑÐ¾ÑÐ¾Ð¹ Ð½Ð°ÑÐ¾Ð´Ð¸ÑÑÑ
   * Ð²Ð¸ÑÑÑÐ°Ð»ÑÐ½ÑÐ¹ ÑÐ¾ÐºÑÑ
   */
  public scrollToFocused() {
    if (this.state.selection.focusedCell) {
      this.scrollTo(this.state.selection.focusedCell);
    }
  }

  /**
   * ÐÑÐ¾ÐºÑÑÑÐºÐ° Ðº ÑÐºÐ°Ð·Ð°Ð½Ð½Ð¾Ð¹ ÑÑÐµÐ¹ÐºÐµ.
   * ÐÐ±ÑÑÐ½Ð¾ Ð²ÑÐ·ÑÐ²Ð°ÐµÐ¼ Ð¿Ð¾ÑÐ»Ðµ Ð¾Ð±ÑÐ°Ð±Ð¾ÑÐºÐ¸ Ð½Ð°Ð¶Ð°ÑÐ¸Ñ ÐºÐ»Ð°Ð²Ð¸ÑÐ¸, ÑÑÐ¾Ð±Ñ Ð±ÑÐ»Ð° Ð²Ð¸Ð´Ð½Ð° ÑÑÑÐ¾ÐºÐ°
   * Ñ ÑÐ¾ÐºÑÑÐ¾Ð¼.
   * Ð¢Ð°ÐºÐ¶Ðµ Ð²ÑÐ·ÑÐ²Ð°ÐµÑÑÑ Ð¿Ð¾ÑÐ»Ðµ ÐºÐ»Ð¸ÐºÐ° Ð¼ÑÑÑÑ Ð¿Ð¾ ÑÑÐµÐ¹ÐºÐµ.. ÐÑÐ»Ð¸ ÑÑÐµÐ¹ÐºÐ° Ð²Ð¸Ð´Ð½Ð° Ð½Ð° ÑÐºÑÐ°Ð½Ðµ
   * ÑÐ¾Ð»ÑÐºÐ¾ ÑÐ°ÑÑÐ¸ÑÐ½Ð¾ - Ð½ÐµÐ¼Ð½Ð¾Ð³Ð¾ ÑÐºÑÐ¾Ð»Ð»Ð¸Ð¼.
   * @param  cellPos ÐÐ¾Ð·Ð¸ÑÐ¸Ñ ÑÑÐµÐ¹ÐºÐ¸, Ðº ÐºÐ¾ÑÐ¾ÑÐ¾Ð¹ Ð¿ÑÐ¾ÐºÑÑÑÐ¸Ð²Ð°ÐµÐ¼ Ð³ÑÐ¸Ð´
   */
  public scrollTo(cellPos: CellPosition) {
    if (!cellPos) {
      return;
    }
    // ÐÑÐ´ÐµÐ¼ ÑÑÐ¸ÑÐ°ÑÑ, ÑÑÐ¾ Ð½Ð°Ð¼ Ð½ÑÐ¶Ð½Ð° ÑÐ¾Ð»ÑÐºÐ¾ ÑÑÐµÐ¹ÐºÐ° Ñ ÑÐ¾ÐºÑÑÐ¾Ð¼
    // Ð¡Ð¾ÑÑÐ°Ð½Ð¸Ð¼ Ð²ÑÑÐ¾ÑÑ ÑÑÑÐ¾Ðº.. Ð¢.Ðº. Ð¾Ð½Ð¸ Ð¼Ð¾Ð³ÑÑ Ð±ÑÑÑ ÐµÑÐµ Ð½Ðµ ÑÐ¾ÑÑÐ°Ð½ÐµÐ½Ñ,
    // ÐµÑÐ»Ð¸ Ð½Ðµ ÑÐ»ÑÑÐ¸Ð»Ð¾ÑÑ Ð¿ÑÐ¾ÐºÑÑÑÐºÐ¸
    this.saveRowHeights();
    let ri = cellPos.rowIndex;
    let fieldName = cellPos.fieldName;

    // ÑÐ½Ð°ÑÐ°Ð»Ð° Ð¿Ð¾ Ð²ÐµÑÑÐ¸ÐºÐ°Ð»Ð¸
    let scrollTop = this.scroller.scrollTop;
    let scrollLeft = this.scroller.scrollLeft;
    let scrollAreaHeight = this.scroller.viewPortHeight;
    let scrollAreaWidth =  this.scroller.viewPortWidth;

    let rowTop = this.RC.getRowTop(ri);
    let rowBottom = rowTop + this.RC.getRowHeight(ri);

    if (rowTop < scrollTop) {
      this.scroller.scrollTo(-1, rowTop);
    } else {
      if (rowBottom > (scrollTop + scrollAreaHeight)) {
        this.scroller.scrollTo(-1, rowBottom - scrollAreaHeight);
      }
    }

    // Ð¢ÐµÐ¿ÐµÑÑ Ð¿Ð¾ Ð³Ð¾ÑÐ¸Ð·Ð¾Ð½ÑÐ°Ð»Ð¸
    // ÐÐ¾Ð·ÑÐ¼ÐµÐ¼ ÑÑÑÐ¾ÐºÑ
    this.displayedRowsCenter.some(row => {
      if (row.row === cellPos.row) {
        const hPos = row.cellHorizontalPos(fieldName);
        if (hPos && hPos.l < scrollLeft) {
          this.scroller.scrollTo(hPos.l);
        }
        if (hPos && hPos.r > scrollLeft + scrollAreaWidth) {
          this.scroller.scrollTo(hPos.r - scrollAreaWidth);
        }
        return true;
      }
      return false
    });
  }

  public scrollToTop() {
    this.scroller.scrollTo(-1, 0);
  }

  protected toggleClass(v: boolean, c: string) {
    if (v) {
      this.elementRef.nativeElement.classList.add(c);
    } else {
      this.elementRef.nativeElement.classList.remove(c);
    }
  }

  /**
   * ÐÑÐ¾Ð²ÐµÑÐ¸ÑÑ, Ð½ÑÐ¶Ð½Ð¾ Ð»Ð¸ Ð¾ÑÐ¾Ð±ÑÐ°Ð·Ð¸ÑÑ Ð»ÐµÐ²ÑÑ Ð¸Ð»Ð¸ Ð¿ÑÐ°Ð²ÑÑ Ð¾Ð±Ð»Ð°ÑÑÑ Ð´Ð»Ñ Ð·Ð°ÑÐ¸ÐºÑÐ¸ÑÐ¾Ð²Ð°Ð½Ð½ÑÑ
   * ÐºÐ¾Ð»Ð¾Ð½Ð¾Ðº
   * @param  xx         [description]
   * @param  scrollRect [description]
   * @param  target     [description]
   * @return            [description]
   */
  protected checkParts(xx: number, scrollRect: any, target: any) {
    // ÐÐµÑÐµÐ³Ð¾ Ð¿ÑÐ¾Ð²ÐµÑÑÑÑ Ð² ÑÑÐ¾Ð¹ ÑÐµÐ°Ð»Ð¸Ð·Ð°ÑÐ¸Ð¸
  }

  /**
   * ÐÑÐ´ÐµÐ»ÐµÐ½Ð¸Ðµ Ð·Ð°Ð´Ð°Ð½Ð½Ð¾Ð¹ ÑÑÑÐ¾ÐºÐ¸
   * @param  r ÐÐ°Ð´Ð°Ð½Ð½Ð°Ñ ÑÑÑÐ¾ÐºÐ°
   * @return   ÐÐ°Ð¹Ð´ÐµÐ½Ð° Ð»Ð¸ Ð·Ð°Ð´Ð°Ð½Ð½Ð°Ñ ÑÑÑÐ¾ÐºÐ° Ð² ÑÐ¿Ð¸ÑÐºÐµ Ð¾ÑÐ¾Ð±ÑÐ°Ð¶Ð°ÐµÐ¼ÑÑ ÑÑÑÐ¾Ðº
   */
  public locateRow(r: any): boolean {
    return this.state.locateRow(r);
  }

  /**
   * ÐÑÐ´ÐµÐ»ÐµÐ½Ð¸Ðµ ÑÑÑÐ¾ÐºÐ¸ Ð¿Ð¾ Ð·Ð°Ð´Ð°Ð½Ð½Ð¾Ð¼Ñ Ð·Ð½Ð°ÑÐµÐ½Ð¸Ñ ÐºÐ»ÑÑÐµÐ²Ð¾Ð³Ð¾ Ð¿Ð¾Ð»Ñ
   * @param  keyValue ÐÐ½Ð°ÑÐµÐ½Ð¸Ðµ ÐºÐ»ÑÑÐµÐ²Ð¾Ð³Ð¾ Ð¿Ð¾Ð»Ñ
   * @param  keyField ÐÐ»ÑÑÐµÐ²Ð¾Ðµ Ð¿Ð¾Ð»Ðµ. ÐÑÐ»Ð¸ Ð½Ðµ Ð·Ð°Ð´Ð°Ð½Ð¾, ÑÐ¾ Ð¿Ð¾Ð»Ðµ Ð±ÐµÑÐµÑÑÑ Ð¸Ð· settings
   * @return          ÐÐ°Ð¹Ð´ÐµÐ½Ð° Ð»Ð¸ ÑÑÑÐ¾ÐºÐ° Ñ Ð·Ð°Ð´Ð°Ð½Ð½ÑÐ¼ ÐºÐ»ÑÑÐ¾Ð¼
   */
  public locateByKey(keyValue: any, keyField: string = '') {
    this.state.locateByKey(keyValue, keyField);
  }

  /**
   * ÐÑÐ¸ÑÑÐºÐ° Ð²ÑÐ´ÐµÐ»ÐµÐ½Ð¸Ñ
   */
  public clearSelection() {
    this.state.clearSelection();
  }

  /**
   * Ð¢ÑÐµÐº Ð¾ÑÐ¾Ð±ÑÐ°Ð¶Ð°ÐµÐ¼ÑÑ Ð·Ð°Ð¿Ð¸ÑÐµÐ¹ Ð´Ð»Ñ Ð±Ð¾Ð»ÐµÐµ Ð±ÑÑÑÑÐ¾Ð³Ð¾ ÑÐµÐ½Ð´ÐµÑÐ° Ð°Ð½Ð³ÑÐ»ÑÑÐ¾Ð¼
   * @param  index ÐÐ½Ð´ÐµÐºÑ ÑÑÑÐ¾ÐºÐ¸
   * @param  data  ÐÐ°Ð½Ð½ÑÐµ ÑÑÑÐ¾ÐºÐ¸
   */
  public trackRow(index: number, data: any) {
    return data;
  }

  /**
   * ÐÑÐ¸ Ð¸Ð·Ð¼ÐµÐ½ÐµÐ½Ð¸Ð¸ ÑÐ°Ð·Ð¼ÐµÑÐ¾Ð² Ð¾ÐºÐ½Ð° Ð²ÑÐ·ÑÐ²Ð°ÐµÑÑÑ ÑÑÐ¾Ñ Ð¼ÐµÑÐ¾Ð´.
   * ÐÑÐ»Ð¸ ÑÐ°Ð·Ð¼ÐµÑ ÐºÐ¾Ð¼Ð¿Ð¾Ð½ÐµÐ½ÑÐ° Ð¸Ð·Ð¼ÐµÐ½ÑÐµÑÑÑ ÐºÐ°ÐºÐ¸Ð¼Ð¸-ÑÐ¾ Ð´ÐµÐ¹ÑÑÐ²Ð¸ÑÐ¼Ð¸ Ð¿Ð¾Ð»ÑÐ·Ð¾Ð²Ð°ÑÐµÐ»Ñ Ð¿Ð¾Ð¼Ð¸Ð¼Ð¾
   * Ð¸Ð·Ð¼ÐµÐ½ÐµÐ½Ð¸Ñ ÑÐ°Ð·Ð¼ÐµÑÐ¾Ð² Ð¾ÐºÐ½Ð° Ð±ÑÐ°ÑÐ·ÐµÑÐ° (Ð²ÑÑÐ¿Ð¾ÑÑÐ°), ÑÐ¾ Ð½ÐµÐ¾Ð±ÑÐ¾Ð´Ð¸Ð¼Ð¾ Ð²ÑÐ·ÑÐ²Ð°ÑÑ ÑÑÐ¾Ñ
   * Ð¼ÐµÑÐ¾Ð´.
   * Ð­ÑÐ¾Ñ Ð¼ÐµÑÐ¾Ð´ ÑÐ°ÐºÐ¶Ðµ ÑÐ»ÐµÐ´ÑÐµÑ Ð²ÑÐ·ÑÐ²Ð°ÑÑ Ð¿ÑÐ¸ drop ÐºÐ¾Ð»Ð¾Ð½ÐºÐ¸, Ñ.Ðº. ÑÐ°Ð·Ð¼ÐµÑ ÑÐµÐ½ÑÑÐ°Ð»ÑÐ½Ð¾Ð¹
   * Ð¾Ð±Ð»Ð°ÑÑÐ¸ Ð¼ÐµÐ½ÑÐµÑÑÑ, ÐµÑÐ»Ð¸ ÐºÐ¾Ð»Ð¾Ð½ÐºÐ° Ð¿ÐµÑÐµÐ±ÑÐ¾ÑÐµÐ½Ð° Ð² Ð»ÐµÐ²ÑÑ Ð¸Ð»Ð¸ Ð¿ÑÐ°Ð²ÑÑ ÑÐ¸ÐºÑÐ¸ÑÐ¾Ð²Ð°Ð½Ð½ÑÑ
   * Ð¾Ð±Ð»Ð°ÑÑÑ.
   * @param  update_page=false [description]
   * @return                   [description]
   */
  public checkSize(update_page = false): boolean {
    // Ð£ÐºÐ°Ð·ÑÐ²Ð°ÐµÐ¼ ÑÐ¸ÑÐ¸Ð½Ñ ÐºÐ»Ð¸ÐµÐ½ÑÑÐºÐ¾Ð¹ Ð¾Ð±Ð»Ð°ÑÑÐ¸.
    // ÐÑÐ»Ð¸ Ð¾Ð½Ð° Ð¸Ð·Ð¼ÐµÐ½ÐµÐ½Ð°, ÑÐ¾ Ð² ÑÐµÑÑÐµÑÐµ Ð±ÑÐ´ÐµÑ Ð²ÑÐ·Ð²Ð°Ð½ updateLayouts

    if (this.state.checkClientWidth(this.scroller.viewPortWidth) ||
        this.state.checkClientHeight(this.scroller.viewPortHeight)) {
      this.need_recalc_page = true;
      if (this._initialized) {
        this.updatePage('checkSize', true);
      }
      return true;
    }

    if (update_page) {
      this.updatePage('checkSize2', true);
    }

    return false;
  }

  // ÐÐ·Ð¼ÐµÐ½ÐµÐ½Ð¸Ðµ ÑÐ°Ð·Ð¼ÐµÑÐ° Ð¾ÐºÐ½Ð°
  protected windowResize(e: any) {
    this.checkSize(true);
  }

  /**
   * ÐÐ¾ÐºÐ°Ð·Ð°ÑÑ ÐºÐ½Ð¾Ð¿ÐºÑ Ð·Ð°Ð³Ð¾Ð»Ð¾Ð²ÐºÐ° Ð´Ð»Ñ ÐºÐ¾Ð»Ð¾Ð½ÐºÐ¸ Ð¿Ð¾ Ð·Ð°Ð´Ð°Ð½Ð½Ð¾Ð¼Ñ Ð¿Ð¾Ð»Ñ
   * @param  fieldName ÐÐ°Ð´Ð°Ð½Ð½Ð¾Ðµ Ð¿Ð¾Ð»Ðµ
   */
  public showHeaderBtn(fieldName: string) {
    this.headerParts.forEach(p => {
      p.showHeaderBtn(fieldName);
    });
  }

  /**
   * Data sorting
   * @param  sortings List of sortings
   */
  public sort(sortings: SortInfo[], update: boolean = true) {
    this.state.sort(sortings, update);
  }

  public clearSorting(update: boolean = true) {
    this.state.sort([], update);
  }

  /**
   * Data filtering
   */
  public filter(filters: Filter[], update: boolean = true) {
    this.state.filter(filters, update);
  }

  public filterToString(filter: Filter) {
    return filter.toString(this.intl, this.state.dataSource.valueFormatter);
  }

  public filterClosed(result: any) {
    if (!result) {
      this.hideHeaderBtns();
    }
  }

  public setFilter(f: any) {
    this.state.setFilter(f);
  }

  public resetFilter(f: any) {
    this.state.resetFilter(f);
  }

  protected getFilterComponentType(filter: Filter): any {
    let filterType: any = FilterTextComponent;

    if (filter.type === ColumnType.NUMBER) {
      filterType = FilterNumberComponent;
    }
    if (filter.type === ColumnType.DATETIME) {
      filterType = FilterDateComponent;
    }
    if (filter.type === ColumnType.BOOLEAN) {
      filterType = FilterBooleanComponent;
    }

    const col: Column = this.state.columnByFieldName(filter.fieldName);

    if (!col) {
      return null;
    }

    if (col.filterComponentType !== null) {
      filterType = col.filterComponentType;
    }
    return filterType;
  }

  protected showFilter(e: any) {
    if (this.menuStarter) {
      this.menuStarter.finish();
    }
    let l = e.target.tagName === 'SPAN' ? e.target.parentElement : e.target;

    if (this.filterPopup.visible) {
      if (this.filterPopup.filter.fieldName === e.filter.fieldName) {
        this.filterPopup.closePopup();
        return;
      } else {
        this.filterPopup.closePopup();
      }
    }

    this.hideHeaderBtns();
    this.showHeaderBtn(e.filter.fieldName);

    const filterType = this.getFilterComponentType(e.filter);

    if (filterType !== null) {
      setTimeout(() => {
        this.filterPopup.showByTarget(l, e.filter, filterType, this.state.model);
      }, 10);
    }
  }

  public focus() {
    this.scroller.focus();
  }

  protected get appearanceClass(): string {
    return this.state.sta.class;
  }

  /**
   * Ð£ÑÑÐ°Ð½Ð¾Ð²ÐºÐ° ÐºÐ»Ð°ÑÑÐ° Ð²Ð½ÐµÑÐ½ÐµÐ³Ð¾ Ð²Ð¸Ð´Ð°
   * @param  appearanceClass ÐÐ»Ð°ÑÑ, ÐºÐ¾ÑÐ¾ÑÑÐ¹ Ð±ÑÐ´ÐµÑ Ð¿ÑÐ¸Ð¼ÐµÐ½ÐµÐ½ Ðº ÐºÐ¾Ð¼Ð¿Ð¾Ð½ÐµÐ½ÑÑ
   */
  public setAppearance() {

    const appearanceClass = this.appearanceClass;

    if (appearanceClass === this._currentAppearance) {
      // ÐÐµÐ· Ð¸Ð·Ð¼ÐµÐ½ÐµÐ½Ð¸Ð¹
      return;
    }

    if (this._currentAppearance !== '') {
      // Ð£Ð±Ð¸ÑÐ°ÐµÐ¼ Ð´Ð¾Ð±Ð°Ð²Ð»ÐµÐ½Ð½ÑÐ¹
      this.elementRef.nativeElement.classList.remove(this._currentAppearance);
    }

    this.elementRef.nativeElement.classList.add(appearanceClass);
    this._currentAppearance = appearanceClass;
  }

  public menuItemClick(e: any) {
    const col = e.target;
    const action = e.action;

    if (action === MenuAction.SORT_ASC) {
      this.sort([new SortInfo(col.fieldName, SortType.ASC)]);
      return;
    }
    if (action === MenuAction.SORT_DESC) {
      this.sort([new SortInfo(col.fieldName, SortType.DESC)]);
      return;
    }
    if (action === MenuAction.HIDE) {
      this.state.hideColumn(col);
      return;
    }

    this.menuAction.emit(e);
  }

  ngOnInit() {
    // ÐÑÐºÐ»ÑÑÐ°ÐµÐ¼ Ð´ÐµÑÐµÐºÑÐ¾Ñ Ð¿Ð¾ Ð½Ð°ÑÑÑÐ¾Ð¹ÐºÐµ.
    // ÐÐµÐ· Ð¾ÑÐºÐ»ÑÑÐµÐ½Ð¸Ñ Ð²ÑÑ ÑÐ°Ð±Ð¾ÑÐ°ÐµÑ, Ð½Ð¾ Ð¼Ð½Ð¾Ð³Ð¾ ÑÐµÐ³Ð¾ Ð¼ÐµÐ»ÑÐºÐ°ÐµÑ Ð½Ðµ Ð²Ð¾Ð²ÑÐµÐ¼Ñ..
    if (this.state.settings.changeDetectionMode === DetectionMode.MANUAL) {
      this.changeDetector.detach();
    }

    // Grid appearance
    this.setAppearance();

    if (this.state.iOS || this.state.android) {
      this.elementRef.nativeElement.classList.add('true-fix-touch');
    }

    if (this.state.IE) {
      this.elementRef.nativeElement.classList.add('true-fix-ie');
    }

    this._settingsDiffer = this.keyValueDiffers.find(this.state.settings).create();
    this._appearanceDiffer = this.keyValueDiffers.find(this.state.settings.appearance).create();

    this._settingsDiffer.diff(this.state.settings);
    this._appearanceDiffer.diff(this.state.settings.appearance);

    this.updateData();
    this._initialized = true;
  }

  ngAfterContentInit() {
    //
  }

  ngAfterViewInit() {

    // Ð¡Ð¾ÑÑÐ°Ð½ÑÐµÐ¼ ÑÐ¸ÑÐ¸Ð½Ñ
    this.state.checkClientWidth(this.scroller.viewPortWidth);

    // ÐÐ° ÑÑÐ¾Ñ Ð¼Ð¾Ð¼ÐµÐ½Ñ Ð¼Ð¾Ð¶ÐµÑ Ð±ÑÑÑ Ð½ÐµÐ¸Ð·Ð²ÐµÑÑÐ½Ð° Ð²ÑÑÐ¾ÑÐ° Ð³ÑÐ¸Ð´Ð°. ÐÐ¾ÑÐ¾ÑÐ°Ñ Ð½Ð°Ð¼ Ð¾ÑÐµÐ½Ñ Ð½ÑÐ¶Ð½Ð°!
    // ÐÐ±ÑÑÐ½Ð¾ ÑÑÐ¾ ÑÐ»ÑÑÐ°ÐµÑÑÑ Ð¿ÑÐ¸ ÑÐ»Ð¾Ð¶Ð½Ð¾Ð¹ ÑÐ°Ð·Ð¼ÐµÑÐºÐµ, ÐºÐ¾Ð³Ð´Ð° ÑÐ°Ð·Ð¼ÐµÑ Ð·Ð°Ð´Ð°Ð½ Ð² Ð¿ÑÐ¾ÑÐµÐ½ÑÐ°Ñ Ð¾Ñ
    // ÑÐ¾Ð´Ð¸ÑÐµÐ»ÑÑÐºÐ¾Ð³Ð¾ ÑÐ»ÐµÐ¼ÐµÐ½ÑÐ°.
    let vh = this.scroller.viewPortHeight;
    if (vh <= this.state.settings.rowHeight) {
      // Ð­ÑÐ¾ ÑÑÐ»Ð¾Ð²Ð¸Ðµ Ð¿Ð¾ÐºÐ°Ð·ÑÐ²Ð°ÐµÑ, ÑÑÐ¾ ÑÑÐ¾-ÑÐ¾ Ð½Ðµ ÑÐ°Ðº.
      // ÐÑ Ð½ÐµÐ¼Ð½Ð¾Ð³Ð¾ Ð¾ÑÐ»Ð¾Ð¶Ð¸Ð¼ Ð¿ÐµÑÐ²ÑÐ¹ ÑÐµÐ½Ð´ÐµÑ Ð´Ð°Ð½Ð½ÑÑ.
      // ÐÑ ÑÐ²Ð¸Ð´Ð¸Ð¼ Ð±ÐµÐ»ÑÐ¹ ÑÐºÑÐ°Ð½ Ð¿ÐµÑÐµÐ´ ÑÐµÐ¼ ÐºÐ°Ðº ÑÐ²Ð¸Ð´Ð¸Ð¼ Ð´Ð°Ð½Ð½ÑÐµ.
      setTimeout(() => this.renderData());
    } else {
      // ÐÑÐ´ÐµÐ¼ Ð¾Ð¿ÑÐ¸Ð¼Ð¸ÑÑÐ°Ð¼Ð¸. ÐÐ¾Ð·Ð¼Ð¾Ð¶Ð½Ð¾, Ð½Ð°Ñ ÑÐ¿Ð°ÑÑÑ ngDoCheck
      this.renderData();
    }

    // ÐÐ°Ð¿Ð¾Ð¼Ð¸Ð½Ð°ÐµÐ¼ ÑÐµÐºÑÑÑÑ Ð²ÑÑÐ¾ÑÑ Ð²ÑÑÐ¿Ð¾ÑÑÐ°
    this.state.checkClientHeight(vh)

    // ÐÐ¾Ð±Ð°Ð²Ð»ÑÐµÐ¼ Ð¿Ð°ÑÑÐ¸Ð²Ð½ÑÐµ ÑÐ»ÑÑÐ°ÑÐµÐ»Ð¸ ÑÐ°Ñ-ÑÐ¾Ð±ÑÑÐ¸Ð¹
    this.addTouchListeners(this.gridData.nativeElement);

    // Ð¡Ð»ÐµÐ´Ð¸Ð¼ Ð·Ð° ÑÐ°Ð·Ð¼ÐµÑÐ¾Ð¼ Ð¾ÐºÐ½Ð°, ÑÑÐ¾Ð±Ñ Ð´Ð¾ÑÐµÐ½Ð´ÐµÑÐ¸ÑÑ Ð½ÐµÐ²Ð¸Ð´Ð¸Ð¼ÑÐµ ÑÐ°Ð½ÐµÐµ Ð´Ð°Ð½Ð½ÑÐµ
    this.addWindowResizeListener();
    this._viewInitialized = true;
  }

  protected doCheckParts() {
    // ÐÐ±Ð½Ð¾Ð²Ð»ÑÐµÐ¼ Ð¸Ð·Ð¼ÐµÐ½ÐµÐ½Ð¸Ðµ
    // Ð¤Ð¸Ð»ÑÑÑÑ Ð½Ðµ Ð²Ð¸Ð½Ð¾Ð²aÑÑ, ÑÑÐ¾ Ñ Ð½Ð°Ñ Ð¾ÑÐºÐ»ÑÑÐµÐ½ Ð´ÐµÑÐµÐºÑÐ¾Ñ Ð¸Ð·Ð¼ÐµÐ½ÐµÐ½Ð¸Ð¹
    if (this.filterPopup && this.filterPopup.visible) {
      // ÐÐ¾ÑÑÐ¾Ð¼Ñ Ð´Ð°ÑÐ¼ Ð·Ð½Ð°ÑÑ Ð¾Ð± Ð¸Ð·Ð¼ÐµÐ½ÐµÐ½Ð¸ÑÑ
      this.filterPopup.changes();
    }
  }

  ngDoCheck() {    
    this.doCheckParts();

    // Ð¡Ð²ÐµÑÑÐµÐ¼ Ð½Ð°ÑÑÑÐ¾Ð¹ÐºÐ¸
    const sChanges = this._settingsDiffer.diff(this.state.settings);
    const aChanges = this._appearanceDiffer.diff(this.state.settings.appearance);
    if (sChanges || aChanges) {
      if (this._viewInitialized) {
        this.setAppearance();
        if (!this.checkSize()) {
          this.updateView();
        }
        return;
      }
    }

    // Ð¡Ð²ÐµÑÑÐµÐ¼ Ð²ÑÑÐ¾ÑÑ Ð³ÑÐ¸Ð´Ð°. Ð­ÑÐ¾ Ð½ÐµÐ¼Ð½Ð¾Ð³Ð¾ Ð·Ð°ÑÐ¾ÑÐ¼Ð°Ð¶Ð¸Ð²Ð°ÐµÑ Ð½Ð°Ñ.
    // ÐÐ¾ Ð´Ð°ÐµÑ Ð³Ð°ÑÐ°Ð½ÑÐ¸Ñ, ÑÑÐ¾ Ð´Ð°Ð½Ð½ÑÐµ Ð±ÑÐ´ÑÑ Ð²Ð¾Ð²ÑÐµÐ¼Ñ Ð¾ÑÑÐ¸ÑÐ¾Ð²Ð°Ð½Ñ
    if (this.state.checkClientHeight(this.scroller.viewPortHeight)
        && this._viewInitialized) {
      this.updatePage();
    }
  }

  ngOnDestroy() {
    // ÐÑÐ¿Ð¸ÑÐ°ÑÑÑÑ Ð¾Ñ ÑÐ¾Ð±ÑÑÐ¸Ð¹
    this.destroy$.next(true);
    this.destroy$.unsubscribe();

    super.ngOnDestroy();
  }

  constructor(
    @Inject('gridState') public state: GridStateService,
    protected intl: InternationalizationService,
    protected elementRef: ElementRef,
    protected changeDetector: ChangeDetectorRef,
    protected keyValueDiffers: KeyValueDiffers) {

    super();

    // Locale was changed
    this.intl.onLocaleChanged.pipe(takeUntil(this.destroy$)).subscribe(l => {
        if (this._viewInitialized) {
          this.detectChanges('locale');
        }
    });

    // ÐÐ°Ð¿ÑÐ¾Ñ Ð´Ð°Ð½Ð½ÑÑ Ñ ÑÐ»ÑÑÐ°ÑÐµÐ»Ñ ÑÐ¾Ð±ÑÑÐ¸Ñ
    this.state.events.onDataQuery.pipe(takeUntil(this.destroy$)).subscribe(q => {
      this.dataUpdating = true;
      this.dataQuery.emit(q);
    });

    // ÐÐ°Ð½Ð½ÑÐµ Ð¿Ð¾Ð»ÑÑÐµÐ½Ñ - Ð¾ÑÐ¾Ð±ÑÐ°Ð¶Ð°ÐµÐ¼
    this.state.events.onDataFetch.pipe(takeUntil(this.destroy$)).subscribe(q => {
      if (this._viewInitialized) {
        if (q.resetData) {
          this.scrollToTop();
        }
        this.renderData();
      }
    });

    // ÐÐ·Ð¼ÐµÐ½ÐµÐ½Ñ ÑÐ¸Ð»ÑÑÑÑ Ð¸Ð»Ð¸ ÑÐ¾ÑÑÐ¸ÑÐ¾Ð²ÐºÐ° Ð¸Ð»Ð¸ Ð½Ð°Ð±Ð¾Ñ ÐºÐ¾Ð»Ð¾Ð½Ð¾Ðº Ð¸Ð»Ð¸ Ð³ÑÑÐ¿Ð¿Ð¸ÑÐ¾Ð²ÐºÐ°
    this.state.events.onQueryChanged.pipe(takeUntil(this.destroy$)).subscribe(q => {
      if (this._initialized) {
        this.updateData();
      }
      /*
      ÐÑ Ð±ÑÐ´ÐµÐ¼ Ð·Ð°Ð¿ÑÐ°ÑÐ¸Ð²Ð°ÑÑ Ð·Ð´ÐµÑÑ Ð·Ð°Ð¿ÑÐ¾Ñ. ÐÐ¾ÑÐ¾Ð¼Ñ ÑÑÐ¾ Ð¼Ð¾Ð¶ÐµÑ Ð±ÑÑÑ Ð»ÐµÐ½Ð¸Ð²Ð°Ñ Ð·Ð°Ð³ÑÑÐ·ÐºÐ° ÑÐ¾ÑÐµÑ
      ÐµÐ³Ð¾ Ð¿Ð¾Ð´Ð¿ÑÐ°Ð²Ð¸ÑÑ.
      ÐÐµÑ, ÐµÐ³Ð¾ Ð½ÐµÐ»ÑÐ·Ñ Ð·Ð°Ð¿ÑÐ¾ÑÐ¸ÑÑ, Ð¿Ð¾ÑÐ¾Ð¼Ñ ÑÑÐ¾ Ñ Ð½ÐµÐ³Ð¾ ÑÐ¶Ðµ Ð¸Ð´ÐµÐ½ÑÐ¸ÑÐ¸ÐºÐ°ÑÐ¾Ñ, Ñ ÐºÐ¾ÑÐ¾ÑÑÐ¼ Ð¼Ñ Ð·Ð°Ð¿ÑÐ¾ÑÐ¸Ð»Ð¸
      Ð¸ Ð¾Ð½ Ð´Ð»Ñ Ð½Ð°Ñ Ð²Ð°Ð¶ÐµÐ½ */
      this.queryChanged.emit(this.state.getQuery());
    });

    // Ð¡Ð»ÐµÐ´ÑÐµÑ Ð¾ÑÐ¾Ð±ÑÐ°Ð·Ð¸ÑÑ Ð¾ÐºÐ½Ð¾ ÑÐ¸Ð»ÑÑÑÐ° Ð´Ð»Ñ Ð·Ð°Ð´Ð°Ð½Ð½Ð¾Ð¹ ÐºÐ¾Ð»Ð¾Ð½ÐºÐ¸
    this.state.events.onFilterShow.pipe(takeUntil(this.destroy$)).subscribe(e => this.showFilter(e));

    // ÐÐ·Ð¼ÐµÐ½ÐµÐ½Ð¸Ðµ ÑÐ¿Ð¸ÑÐºÐ° ÑÑÐ¼Ð¼Ð¸ÑÐ¾Ð²Ð°Ð½Ð¸Ð¹ ÐºÐ¾Ð»Ð¾Ð½ÐºÐ¸
    this.state.events.onSummariesChanged.pipe(takeUntil(this.destroy$)).subscribe(v => {
      this.state.updateSummaries();
      this.updateView();
    });

    this.state.events.onHeaderContextMenu.pipe(takeUntil(this.destroy$)).subscribe(e => {
      // Ð­ÑÐ¾ Ð½ÑÐ¶Ð½Ð¾ Ð²ÑÐ½ÐµÑÑÐ¸ ÐºÑÐ´Ð°-Ð½Ð¸Ð±ÑÐ´Ñ Ð¿Ð¾Ð´Ð°Ð»ÑÑÐµ
      if (this.state.settings.enableHeaderContextMenu) {
        const actions = this.state.settings.headerContextMenuActions;
        const sorted = this.state.dataSource.sortedByField(e.column.fieldName);
        if (actions.length > 0) {
          actions.forEach(a => {
            if (a === MenuAction.SORT_ASC) {
              a.disabled = sorted && sorted.sortType === SortType.ASC;
            }
            if (a === MenuAction.SORT_DESC) {
              a.disabled = sorted && sorted.sortType === SortType.DESC;
            }
          });
          this.menuStarter.start(e.event, this.state.settings.headerContextMenuActions, e.column);
          this.detectChanges();
          e.event.preventDefault();
        }
      }
      this.headerContextMenu.emit(e);
    });

    // Custom cell event
    this.state.events.onCustomCellEvent.pipe(takeUntil(this.destroy$)).subscribe(e => {
      this.customCellEvent.emit(e);
    });
  }
}
